import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UploadCloud, PlayCircle } from "lucide-react";
import { PrimeImportPreview } from "./PrimeImportPreview";
import { toast } from "sonner";

const toB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(file);
});

function Mapping({ headers, mapping, setMapping, fields }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="prime-mapping">
      {Object.entries(fields).map(([k, label]) => (
        <label key={k} className="text-xs text-slate-600">{label}
          <select value={mapping[k] || ""} onChange={(e) => setMapping({ ...mapping, [k]: e.target.value })}
            className="mt-1 w-full h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid={`prime-map-${k}`}>
            <option value="">— not in file —</option>{headers.filter(Boolean).map((h) => <option key={h} value={h}>{h}</option>)}</select>
        </label>))}
    </div>
  );
}

export function PrimeImportWizard({ org, fields, onApplied }) {
  const [file, setFile] = useState(null);
  const [info, setInfo] = useState(null);
  const [mapping, setMapping] = useState({});
  const [opts, setOpts] = useState({ score_format: "", default_period: "", source: "Farmers Dashboard", clear_fields: [] });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const inspect = async (f, sheet, header_row) => {
    setBusy(true);
    try {
      const { data } = await api.post("/prime/import/inspect", { organization_id: org.id, filename: f.name, content_b64: f.b64, sheet, header_row });
      setInfo(data); setMapping(data.suggested_mapping || {}); setPreview(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!/\.(csv|xlsx)$/i.test(f.name)) { toast.error("Upload a .csv or .xlsx file"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("File is larger than 8 MB"); return; }
    const x = { name: f.name, b64: await toB64(f) }; setFile(x); inspect(x);
  };
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/prime/import/preview", { organization_id: org.id, filename: file.name, content_b64: file.b64, sheet: info.sheet,
        header_row: info.header_row, mapping, ...opts });
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const toggleClear = (k) => setOpts({ ...opts, clear_fields: opts.clear_fields.includes(k) ? opts.clear_fields.filter((x) => x !== k) : [...opts.clear_fields, k] });

  if (preview) return <PrimeImportPreview batch={preview} onBack={() => setPreview(null)} onApplied={onApplied} />;
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <p className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><UploadCloud className="w-4 h-4 text-brand" />1. Upload a Farmers Dashboard agent list</p>
        <p className="text-xs text-slate-500 mb-2">CSV or XLSX, up to 8 MB. Nothing changes until you click Apply Updates.</p>
        <input type="file" accept=".csv,.xlsx" onChange={onFile} data-testid="prime-file-input" className="text-sm" />
      </div>
      {info && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            {info.sheets.length > 1 && <label className="text-xs text-slate-600">Worksheet
              <select value={info.sheet} onChange={(e) => inspect(file, e.target.value)} className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="prime-sheet">
                {info.sheets.map((s) => <option key={s}>{s}</option>)}</select></label>}
            <label className="text-xs text-slate-600">Header row
              <Input type="number" min={1} value={info.header_row} onChange={(e) => inspect(file, info.sheet, Number(e.target.value))} className="mt-1 w-24" data-testid="prime-header-row" /></label>
            <span className="text-xs text-slate-500">{info.data_rows} rows below the header</span>
          </div>
          <div className="overflow-auto max-h-48 border border-slate-100 rounded" data-testid="prime-sample">
            <table className="text-[11px] min-w-full">{info.sample.slice(0, 8).map((r, i) => (
              <tbody key={i}><tr className={i + 1 === info.header_row ? "bg-[#006090]/10 font-semibold" : ""}><td className="px-2 text-slate-400">{i + 1}</td>{r.map((c, j) => <td key={j} className="px-2 py-0.5 whitespace-nowrap">{c}</td>)}</tr></tbody>))}</table>
          </div>
          <p className="text-sm font-semibold text-slate-900">2. Map columns</p>
          <Mapping headers={info.headers} mapping={mapping} setMapping={setMapping} fields={fields} />
          <p className="text-sm font-semibold text-slate-900">3. Interpret values</p>
          {info.percent_formatted_columns.length > 0 && <p className="text-xs text-amber-700" data-testid="prime-pct-hint">Excel percentage formatting found in: {info.percent_formatted_columns.join(", ")}. These cells are usually stored as fractions (0.725 = 72.5%).</p>}
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="text-xs text-slate-600">How is the Prime Score stored?
              <select value={opts.score_format} onChange={(e) => setOpts({ ...opts, score_format: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="prime-score-format">
                <option value="">Choose…</option><option value="percent">Percentage — 72.5 or 72.5%</option><option value="fraction">Excel fraction — 0.725</option>
                <option value="points">Achieved ÷ available KPI points</option></select></label>
            <label className="text-xs text-slate-600">Reporting month (if not in file)
              <Input type="month" value={opts.default_period} onChange={(e) => setOpts({ ...opts, default_period: e.target.value })} className="mt-1" data-testid="prime-default-period" /></label>
            <label className="text-xs text-slate-600">Source<Input value={opts.source} onChange={(e) => setOpts({ ...opts, source: e.target.value })} className="mt-1" data-testid="prime-source" /></label>
          </div>
          <div className="text-xs text-slate-600 space-y-1">
            <p>Blank cells keep existing values. To clear them instead (shown separately in the preview):</p>
            <label className="mr-4"><input type="checkbox" checked={opts.clear_fields.includes("score")} onChange={() => toggleClear("score")} data-testid="prime-clear-score" /> Clear Prime Score when blank</label>
            <label><input type="checkbox" checked={opts.clear_fields.includes("level")} onChange={() => toggleClear("level")} data-testid="prime-clear-level" /> Clear Achievement Level when blank</label>
          </div>
          <Button onClick={run} disabled={busy} className="bg-brand hover:bg-brand-strong" data-testid="prime-preview-btn"><PlayCircle className="w-4 h-4 mr-1.5" />Preview matches</Button>
        </div>)}
    </div>
  );
}
